import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Leaf, Flame, Star } from "lucide-react";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { doughBurst } from "@/lib/confetti";
import ItemCard from "@/components/ItemCard";
import CustomizeModal from "@/components/CustomizeModal";
import useActiveBatch from "@/hooks/useActiveBatch";
import BatchStockBadge from "@/components/BatchStockBadge";
import ClosingSoonBanner from "@/components/store/ClosingSoonBanner";
import FloatingMenuNav from "@/components/FloatingMenuNav";
import { toast } from "sonner";

const DIET = [
  { id: "veg", label: "Veg", icon: Leaf },
  { id: "nonveg", label: "Non-veg", icon: Flame },
  { id: "chef_special", label: "Signature", icon: Star },
];

function CategoryTabs({ tabs, tab, setTab }) {
  return (
    <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
      {tabs.map((t) => (
        <button
          key={t.id}
          data-testid={`tab-${t.id}`}
          onClick={() => setTab(t.id)}
          className={`relative whitespace-nowrap px-5 py-2.5 rounded-full font-bold text-sm transition-colors border-2 ${
            tab === t.id ? "text-biga-cream border-biga-ink" : "text-biga-ink border-biga-ink/15 hover:border-biga-ink/40"
          }`}
        >
          {tab === t.id && (
            <motion.span layoutId="tab-pill" className="absolute inset-0 bg-biga-ink rounded-full" transition={{ type: "spring", stiffness: 400, damping: 32 }} />
          )}
          <span className="relative z-10">{t.label}</span>
        </button>
      ))}
    </div>
  );
}

function DietFilters({ diet, toggleDiet }) {
  return (
    <div className="flex gap-2 mt-3 flex-wrap">
      {DIET.map((d) => {
        const Icon = d.icon;
        const active = diet.includes(d.id);
        return (
          <button
            key={d.id}
            data-testid={`diet-${d.id}`}
            onClick={() => toggleDiet(d.id)}
            className={`flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide px-3 py-1.5 rounded-full border-2 transition-colors ${
              active ? "bg-biga-green text-biga-cream border-biga-green" : "border-biga-green/30 text-biga-green hover:border-biga-green"
            }`}
          >
            <Icon size={13} /> {d.label}
          </button>
        );
      })}
    </div>
  );
}

export default function MenuSection() {
  const [menu, setMenu] = useState([]);
  const [tabs, setTabs] = useState([]);
  const [tab, setTab] = useState(null);
  const [diet, setDiet] = useState([]);
  const [customizeItem, setCustomizeItem] = useState(null);
  const { addItem, setOpen } = useCart();
  const activeBatch = useActiveBatch();

  useEffect(() => {
    api.get("/menu").then((r) => setMenu(r.data)).catch(() => {});
    api.get("/categories").then((r) => {
      setTabs(r.data);
      setTab((prev) => prev || r.data[0]?.id || null);
    }).catch(() => {});
  }, []);

  const toggleDiet = (id) =>
    setDiet((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const visible = useMemo(() => {
    let list = menu
      .filter((i) => i.category === tab)
      .map((i) => ({ item: i, size: (i.sizes || []).find((s) => s.price > 0) || null }));
    if (diet.length) list = list.filter(({ item }) => diet.every((d) => {
      if (d === "veg") return item.veg === true || item.veg === "egg";
      if (d === "nonveg") return item.veg === false;
      return item.tags.includes(d);
    }));
    return list;
  }, [menu, tab, diet]);

  const pushToCart = (line, e) => {
    if (!addItem(line)) return;
    if (e) doughBurst(e.clientX, e.clientY);
    toast.success(`${line.name} added to cart`, {
      action: { label: "View cart", onClick: () => setOpen(true) },
    });
  };

  const handleAdd = (item, defaultSize, e) => {
    if (item.price_pending) {
      toast.info("This item is awaiting its approved online price.");
      return;
    }
    if (item.customizable) {
      setCustomizeItem(item);
      return;
    }
    pushToCart(
      {
        key: item.id,
        item_id: item.id,
        name: item.name,
        size: defaultSize?.label || null,
        crust: item.category === "roman" ? "BIGA Crust" : null,
        extras: [],
        half_and_half: null,
        qty: 1,
        unit_price: defaultSize ? defaultSize.price : item.price,
        image: item.image,
      },
      e
    );
  };

  return (
    <section id="menu" className="bg-biga-cream py-16 sm:py-24 px-5 sm:px-8" data-testid="menu-section">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-biga-orange">The Menu</span>
            <h2 className="font-display font-black text-5xl sm:text-7xl leading-[0.9] mt-2 text-biga-ink">
              Order whole<br />pizzas, fresh.
            </h2>
          </div>
          <div className="flex flex-col items-start gap-2"><BatchStockBadge testid="menu-batch-badge" className="text-sm" /><ClosingSoonBanner /></div>
        </div>

        {/* Category tabs */}
        <div className="sticky top-[68px] z-30 -mx-5 px-5 py-3 bg-biga-cream/90 backdrop-blur-md">
          <CategoryTabs tabs={tabs} tab={tab} setTab={setTab} />
          <DietFilters diet={diet} toggleDiet={toggleDiet} />
        </div>

        {tabs.find((t) => t.id === tab)?.subtitle && (
          <p className="mt-6 text-base sm:text-lg text-biga-ink/60 max-w-2xl" data-testid="section-subtitle">
            {tabs.find((t) => t.id === tab).subtitle}
          </p>
        )}

        <motion.div layout className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6 mt-8">
          <AnimatePresence mode="popLayout">
            {visible.map(({ item, size }, i) => (
              <ItemCard key={`${item.id}-${tab}`} item={item} defaultSize={size} index={i} onAdd={handleAdd} activeBatch={activeBatch} />
            ))}
          </AnimatePresence>
        </motion.div>
        {visible.length === 0 && (
          <p className="text-center text-biga-ink/50 font-semibold py-16">No items match these filters.</p>
        )}
      </div>

      <CustomizeModal
        item={customizeItem}
        allItems={menu}
        onClose={() => setCustomizeItem(null)}
        onConfirm={(line, e) => {
          setCustomizeItem(null);
          setTimeout(() => pushToCart(line, e), 30);
        }}
      />
      <FloatingMenuNav tabs={tabs} menu={menu} activeTab={tab} onSelectTab={setTab} />
    </section>
  );
}
